/**
 * React Query hooks for managing shared folders (P1-F5). Keys live under `['sharing', …]` with the
 * gating hooks' keys; a change to a share invalidates that share's queries and the share list, which
 * the Shared root reads too.
 */
import {useEffect, useState} from 'react'
import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {useCognitoAuth} from '@/app/modules/auth'
import {sharingQueryKeys, useSharingAccess} from './hooks'
import {
  acceptShareInvitationById,
  createShare,
  declineShareInvitationById,
  getSharingSettings,
  inviteToShare,
  listEligibleMembers,
  listMyShareInvitations,
  listShareActivity,
  listShareInvitations,
  listShareMembers,
  putSharingSettings,
  removeShareMember,
  revokeShareInvitation,
  stopShare,
  updateShareMember,
} from './managementApi'
import type {InviteTarget, MemberAccess, SharingSettings} from './types'

/** Keys for the management queries. The share list and the feature flag use `sharingQueryKeys`. */
export const shareManagementKeys = {
  share: (shareId: string) => ['sharing', 'share', shareId] as const,
  members: (shareId: string) => ['sharing', 'share', shareId, 'members'] as const,
  invitations: (shareId: string) => ['sharing', 'share', shareId, 'invitations'] as const,
  activity: (shareId: string) => ['sharing', 'share', shareId, 'activity'] as const,
  eligible: (query: string) => ['sharing', 'eligible-members', query] as const,
  pendingInvitations: (sub?: string) =>
    sub === undefined
      ? (['sharing', 'pending-invitations'] as const)
      : (['sharing', 'pending-invitations', sub] as const),
  invitationPreview: (token: string) => ['sharing', 'invitation-preview', token] as const,
  settings: (subscriptionId: string) => ['sharing', 'settings', subscriptionId] as const,
}

/** Refetch the share list, wherever it is shown. */
export function useInvalidateShares() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({queryKey: sharingQueryKeys.shares()})
}

export function useShareMembers(shareId: string | undefined) {
  return useQuery({
    queryKey: shareManagementKeys.members(shareId ?? ''),
    queryFn: () => listShareMembers(shareId!),
    enabled: !!shareId,
  })
}

export function useShareInvitations(shareId: string | undefined) {
  return useQuery({
    queryKey: shareManagementKeys.invitations(shareId ?? ''),
    queryFn: () => listShareInvitations(shareId!),
    enabled: !!shareId,
  })
}

export function useShareActivity(shareId: string | undefined, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: shareManagementKeys.activity(shareId ?? ''),
    queryFn: ({pageParam}) => listShareActivity(shareId!, pageParam as string | undefined),
    getNextPageParam: (page) => page.next || undefined,
    enabled: !!shareId && enabled,
  })
}

/** `value`, once it has stopped changing for `ms`. */
export function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return debounced
}

export function useEligibleMembers(query: string, enabled: boolean) {
  return useQuery({
    queryKey: shareManagementKeys.eligible(query),
    queryFn: () => listEligibleMembers(query),
    enabled,
    staleTime: 60 * 1000,
    retry: false,
  })
}

export function useCreateShare() {
  const invalidateShares = useInvalidateShares()
  return useMutation({
    mutationFn: (folderPath: string) => createShare(folderPath),
    onSuccess: () => invalidateShares(),
  })
}

export function useInviteToShare(shareId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({target, access}: {target: InviteTarget; access: MemberAccess}) =>
      inviteToShare(shareId, target, access),
    onSuccess: () =>
      queryClient.invalidateQueries({queryKey: shareManagementKeys.invitations(shareId)}),
  })
}

export function useRevokeShareInvitation(shareId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (inviteId: string) => revokeShareInvitation(shareId, inviteId),
    onSettled: () =>
      queryClient.invalidateQueries({queryKey: shareManagementKeys.invitations(shareId)}),
  })
}

export function useUpdateShareMember(shareId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({sub, access}: {sub: string; access: MemberAccess}) =>
      updateShareMember(shareId, sub, access),
    onSettled: () =>
      queryClient.invalidateQueries({queryKey: shareManagementKeys.members(shareId)}),
  })
}

export function useRemoveShareMember(shareId: string) {
  const queryClient = useQueryClient()
  const invalidateShares = useInvalidateShares()
  return useMutation({
    mutationFn: (sub: string) => removeShareMember(shareId, sub),
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: shareManagementKeys.members(shareId)})
      invalidateShares()
    },
  })
}

export function useStopShare(shareId: string) {
  const queryClient = useQueryClient()
  const invalidateShares = useInvalidateShares()
  return useMutation({
    mutationFn: () => stopShare(shareId),
    onSuccess: () => {
      queryClient.removeQueries({queryKey: shareManagementKeys.share(shareId)})
      invalidateShares()
    },
  })
}

/** The signed-in user's pending invitations (`GET /v1/share-invitations`), when sharing is on. */
export function usePendingShareInvitations() {
  const {currentUser} = useCognitoAuth()
  const sub = currentUser?.sub || ''
  const {sharing} = useSharingAccess()
  return useQuery({
    queryKey: shareManagementKeys.pendingInvitations(sub),
    queryFn: listMyShareInvitations,
    enabled: !!sub && sharing,
    staleTime: 60 * 1000,
  })
}

export interface AnswerShareInvitation {
  shareId: string
  inviteId: string
  answer: 'accept' | 'decline'
}

/** Accept or decline one of my pending invitations. */
export function useAnswerShareInvitation() {
  const queryClient = useQueryClient()
  const invalidateShares = useInvalidateShares()
  return useMutation<unknown, unknown, AnswerShareInvitation>({
    mutationFn: ({shareId, inviteId, answer}) =>
      answer === 'accept'
        ? acceptShareInvitationById(shareId, inviteId)
        : declineShareInvitationById(shareId, inviteId),
    onSettled: (_data, _error, {answer}) => {
      queryClient.invalidateQueries({queryKey: shareManagementKeys.pendingInvitations()})
      if (answer === 'accept') invalidateShares()
    },
  })
}

export function useSharingSettings(subscriptionId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: shareManagementKeys.settings(subscriptionId ?? ''),
    queryFn: () => getSharingSettings(subscriptionId!),
    enabled: !!subscriptionId && enabled,
    retry: false,
  })
}

export function useSaveSharingSettings(subscriptionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (settings: SharingSettings) => putSharingSettings(subscriptionId, settings),
    onSuccess: (saved) =>
      queryClient.setQueryData(shareManagementKeys.settings(subscriptionId), saved),
  })
}
